import { MetaDataInterface, DatabaseType, StorageType, CloudProvider, Runtime, JavaConfig } from '@/interfaces/entities/entity_blueprints/MetaDataInterface';

/**
 * Default metadata configuration with all features enabled
 * This ensures that if users don't specify certain options, they get sensible defaults
 */
export const DEFAULT_META_DATA_CONFIG: MetaDataInterface = {
  // Runtime environment - defaults to nodejs
  runtime: Runtime.NODEJS,

  // Authentication - enabled by default with both methods
  authentication: true,
  authenticationMethods: {
    emailPassword: true,
    googleAuth: true,
  },

  // File storage - enabled by default
  fileStorage: true,
  storageType: StorageType.AWS_S3,

  // Database - PostgreSQL by default
  databaseType: DatabaseType.POSTGRESQL,

  // Rate limiting - enabled with sensible defaults
  rateLimiterEnabled: true,
  rateLimiterConfig: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    maxRequests: 100, // 100 requests per window
    max: 100,
    standardHeaders: true,
    legacyHeaders: false,
    message: 'Too many requests from this IP, please try again later.',
    skipFailedRequests: false,
  },

  // CORS - enabled with permissive defaults for development
  corsEnabled: true,
  corsConfig: {
    allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS'],
    allowedHeaders: ['Content-Type', 'Authorization', 'X-Requested-With'],
    allowedOrigins: ['http://localhost:3000', 'http://localhost:3001'],
    allowCredentials: true,
    maxAge: 86400, // 24 hours
    preflightContinue: false,
  },

  // WebSocket - enabled by default
  websocketEnabled: true,
  websocketConfig: {
    path: '/ws',
    corsConfig: {
      allowedMethods: ['GET', 'POST'],
      allowedHeaders: ['Content-Type', 'Authorization'],
      allowedOrigins: ['http://localhost:3000', 'http://localhost:3001'],
      allowCredentials: true,
      maxAge: 86400,
      preflightContinue: false,
    },
  },
  // Auto Scaling - disabled by default
  autoScaleConfig: {
    enabled: false,
    cloudProvider: CloudProvider.AWS,
    minInstances: 1,
    maxInstances: 10,
    targetCPUUtilization: 70,
    targetMemoryUtilization: 80,
    cooldownPeriod: 300, // 5 minutes
    scalingPolicy: 'horizontal',
  },

  // RBAC - disabled by default
  rbac: {
    enabled: false,
    roles: [],
    canModify: false,
  },

  // Java configuration - defaults for Java/Spring Boot projects
  javaConfig: {
    javaVersion: '21',
    buildTool: 'maven',
    groupId: 'com.example',
    artifactId: 'app',
    basePackage: 'com.example.app',
    springDependencies: ['spring-web', 'spring-data-jpa', 'lombok', 'validation', 'springdoc-openapi'],
  },
};

/**
 * Merge user metadata with defaults
 * This ensures all required fields are present even if user doesn't specify them
 */
export function mergeWithDefaults(userMetadata: Partial<MetaDataInterface> = {}): MetaDataInterface {
  return {
    ...DEFAULT_META_DATA_CONFIG,
    ...userMetadata,
    // Deep merge for nested objects
    authenticationMethods: {
      ...DEFAULT_META_DATA_CONFIG.authenticationMethods,
      ...userMetadata.authenticationMethods,
    },
    rateLimiterConfig: userMetadata.rateLimiterConfig
      ? {
        ...DEFAULT_META_DATA_CONFIG.rateLimiterConfig,
        ...userMetadata.rateLimiterConfig,
      }
      : DEFAULT_META_DATA_CONFIG.rateLimiterConfig,
    corsConfig: userMetadata.corsConfig
      ? {
        ...DEFAULT_META_DATA_CONFIG.corsConfig,
        ...userMetadata.corsConfig,
      }
      : DEFAULT_META_DATA_CONFIG.corsConfig,
    websocketConfig: userMetadata.websocketConfig
      ? {
        ...DEFAULT_META_DATA_CONFIG.websocketConfig,
        ...userMetadata.websocketConfig,
        corsConfig: userMetadata.websocketConfig.corsConfig
          ? {
            ...DEFAULT_META_DATA_CONFIG.websocketConfig?.corsConfig,
            ...userMetadata.websocketConfig.corsConfig,
          }
          : DEFAULT_META_DATA_CONFIG.websocketConfig?.corsConfig,
      }
      : DEFAULT_META_DATA_CONFIG.websocketConfig,
    autoScaleConfig: userMetadata.autoScaleConfig
      ? {
        ...DEFAULT_META_DATA_CONFIG.autoScaleConfig,
        ...userMetadata.autoScaleConfig,
      }
      : DEFAULT_META_DATA_CONFIG.autoScaleConfig,
    rbac: userMetadata.rbac
      ? {
        ...DEFAULT_META_DATA_CONFIG.rbac,
        ...userMetadata.rbac,
      }
      : DEFAULT_META_DATA_CONFIG.rbac,
    javaConfig: userMetadata.javaConfig
      ? {
        ...DEFAULT_META_DATA_CONFIG.javaConfig as JavaConfig,
        ...userMetadata.javaConfig,
      }
      : DEFAULT_META_DATA_CONFIG.javaConfig,
  };
}

/**
 * Validate metadata configuration
 * Returns validation result with any errors found
 */
export function validateMetadata(metadata: MetaDataInterface): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Validate runtime
  if (metadata.runtime && !Object.values(Runtime).includes(metadata.runtime)) {
    errors.push(`Runtime must be one of: ${Object.values(Runtime).join(', ')}`);
  }

  // Validate authentication methods
  if (metadata.authentication && !metadata.authenticationMethods.emailPassword && !metadata.authenticationMethods.googleAuth) {
    errors.push('At least one authentication method must be enabled when authentication is enabled');
  }

  // Validate rate limiter config
  if (metadata.rateLimiterEnabled && metadata.rateLimiterConfig) {
    if (metadata.rateLimiterConfig.windowMs <= 0) {
      errors.push('Rate limiter windowMs must be greater than 0');
    }
    if (metadata.rateLimiterConfig.maxRequests <= 0) {
      errors.push('Rate limiter maxRequests must be greater than 0');
    }
    if (metadata.rateLimiterConfig.max <= 0) {
      errors.push('Rate limiter max must be greater than 0');
    }
  }

  // Validate CORS config
  if (metadata.corsEnabled && metadata.corsConfig) {
    if (!metadata.corsConfig.allowedMethods || metadata.corsConfig.allowedMethods.length === 0) {
      errors.push('CORS must have at least one allowed method');
    }
    if (!metadata.corsConfig.allowedHeaders || metadata.corsConfig.allowedHeaders.length === 0) {
      errors.push('CORS must have at least one allowed header');
    }
    if (!metadata.corsConfig.allowedOrigins || metadata.corsConfig.allowedOrigins.length === 0) {
      errors.push('CORS must have at least one allowed origin');
    }
  }

  // Validate WebSocket config
  if (metadata.websocketEnabled && metadata.websocketConfig) {
    if (!metadata.websocketConfig.path || metadata.websocketConfig.path.trim() === '') {
      errors.push('WebSocket path cannot be empty');
    }
  }

  // Validate RBAC prerequisites
  if (metadata.rbac?.enabled) {
    if (!metadata.authentication) {
      errors.push('Authentication must be enabled before enabling RBAC');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get metadata configuration for specific runtime
 * This can be used to get runtime-specific defaults
 */
export function getRuntimeDefaults(runtime: Runtime): Partial<MetaDataInterface> {
  const baseDefaults = { ...DEFAULT_META_DATA_CONFIG };

  if (runtime === Runtime.PYTHON) {
    // Python-specific defaults
    return {
      ...baseDefaults,
      // Python might have different default configurations
      corsConfig: {
        ...baseDefaults.corsConfig!,
        allowedOrigins: ['http://localhost:8000', 'http://localhost:3000'],
      },
    };
  }

  if (runtime === Runtime.JAVA) {
    // Java/Spring Boot-specific defaults
    return {
      ...baseDefaults,
      corsConfig: {
        ...baseDefaults.corsConfig!,
        allowedOrigins: ['http://localhost:8080', 'http://localhost:3000'],
      },
    };
  }

  // Node.js defaults (same as base)
  return baseDefaults;
}
